// ============================================================================
// ITALY TAX CALCULATION - IRPEF 2024
// Source: Agenzia delle Entrate
// ============================================================================

import { TaxCalculationResult, CountryTaxData, DeductionBreakdown } from "./types";
import { calculateTaxByBrackets } from "./utils";

export const ITALY_TAX_CONFIG: CountryTaxData = {
  socialSecurityRate: 9.49, // INPS employee contribution
  taxBrackets: [
    { min: 0, max: 28000, rate: 23 },
    { min: 28000, max: 50000, rate: 35 },
    { min: 50000, max: Infinity, rate: 43 },
  ],
  personalAllowance: 8174, // No tax area for employees
  marriedAllowance: 0,
  childAllowances: [0, 0, 0], // Assegno unico handled separately
  currency: "EUR",
};

// INPS contribution rates
const INPS_RATE = 9.49; // Employee contribution

// Detrazioni (tax credits)
const DETRAZIONI = {
  // Detrazione per redditi da lavoro dipendente (work income credit)
  lavoro: {
    base: 1880, // Up to 15,000€
    coefficient: 1190, // 15,000-28,000€
    reduced: 65, // Reduced coefficient for higher incomes
  },
  // Regional and municipal surcharges (average)
  addizionaleRegionale: 1.23, // Average rate
  addizionaleComunale: 0.8, // Average rate
};

export interface ItalyTaxParams {
  grossIncome: number;
  familyStatus: string;
  dependents: number;
}

/**
 * Calcula el IRPEF y contributi INPS de Italia
 * 
 * Características del sistema italiano:
 * - IRPEF con 3 tramos (desde 2024)
 * - Detrazioni per lavoro dipendente
 * - Addizionali regionali y comunales
 * - Assegno Unico para hijos (no incluido aquí)
 */
export function calculateItalyTax({
  grossIncome,
  familyStatus,
  dependents,
}: ItalyTaxParams): TaxCalculationResult {
  const config = ITALY_TAX_CONFIG;

  // ============================================================================
  // SOCIAL SECURITY (Contributi INPS)
  // ============================================================================
  
  const socialSecurity = (grossIncome * INPS_RATE) / 100;

  // ============================================================================
  // INCOME TAX (IRPEF)
  // ============================================================================

  // Taxable income (after INPS deduction)
  const redditoImponibile = grossIncome - socialSecurity;

  // IRPEF base
  const irpefLorda = calculateTaxByBrackets(redditoImponibile, config.taxBrackets);

  // Detrazione per lavoro dipendente
  let detrazioneLavoro = 0;
  if (redditoImponibile <= 15000) {
    detrazioneLavoro = DETRAZIONI.lavoro.base;
  } else if (redditoImponibile <= 28000) {
    // Formula: 1.910 + 1.190 × (28.000 - reddito) / 13.000
    detrazioneLavoro = 1910 + (DETRAZIONI.lavoro.coefficient * (28000 - redditoImponibile)) / 13000;
  } else if (redditoImponibile <= 50000) {
    // Formula: 1.910 × (50.000 - reddito) / 22.000
    detrazioneLavoro = (1910 * (50000 - redditoImponibile)) / 22000;
  }

  // IRPEF netta
  const irpefNetta = Math.max(0, irpefLorda - detrazioneLavoro);

  // Addizionali regionali e comunali
  const addizionaleRegionale = (redditoImponibile * DETRAZIONI.addizionaleRegionale) / 100;
  const addizionaleComunale = (redditoImponibile * DETRAZIONI.addizionaleComunale) / 100;

  const totalTax = irpefNetta + addizionaleRegionale + addizionaleComunale;
  const taxRate = grossIncome > 0 ? (totalTax / grossIncome) * 100 : 0;

  // Build breakdown with Italian labels
  const breakdown: DeductionBreakdown[] = [];

  if (irpefNetta > 0) {
    breakdown.push({
      id: "irpef",
      amount: irpefNetta,
      rate: grossIncome > 0 ? (irpefNetta / grossIncome) * 100 : 0,
      type: "tax",
    });
  }

  if (addizionaleRegionale + addizionaleComunale > 0) {
    breakdown.push({
      id: "addizionali",
      amount: addizionaleRegionale + addizionaleComunale,
      rate: grossIncome > 0 ? ((addizionaleRegionale + addizionaleComunale) / grossIncome) * 100 : 0,
      type: "tax",
    });
  }

  if (socialSecurity > 0) {
    breakdown.push({
      id: "inps",
      amount: socialSecurity,
      rate: grossIncome > 0 ? (socialSecurity / grossIncome) * 100 : 0,
      type: "social",
    });
  }

  return {
    tax: totalTax,
    taxRate,
    socialSecurity,
    allowances: detrazioneLavoro,
    breakdown,
  };
}
